import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { User } from 'types/interfaces/user/User';

import { Dialog, DialogsResponse, Message } from 'api/DialogsApi';
import { sortMessagesByDate } from 'helpers/dates';

type MessengerState = {
  dialogs: DialogsResponse;
  messages: Record<
    string,
    {
      next: string | null;
      messages: Message[];
      contact: User | null;
      isMatched?: boolean;
    }
  >;
  messagesLoading: boolean;
};

const initialState: MessengerState = {
  dialogs: {
    data: [],
    next: null,
  },
  messages: {},
  messagesLoading: false,
};

const updateDialogWithNewMessageMapper =
  (newMessage: Message) => (dialog: Dialog) => {
    const isDialogWithProperContact =
      dialog.contact.id === newMessage.contact_id;

    if (!isDialogWithProperContact) {
      return dialog;
    }

    return {
      ...dialog,
      unread_count: newMessage.is_incoming
        ? dialog.unread_count + 1
        : dialog.unread_count,
      last_message: newMessage,
    };
  };

const setMessageReadAt =
  (contactId: number, isIncoming: boolean) => (message: Message) => {
    if (message.contact_id !== contactId) {
      return message;
    }

    if (message.is_incoming !== isIncoming) {
      return message;
    }

    return {
      ...message,
      ...(!message.read_at && { read_at: new Date().toUTCString() }),
    };
  };

const setDialogLastMessageReadAt = (contactId: number) => (dialog: Dialog) => {
  if (dialog.last_message.contact_id !== contactId) {
    return dialog;
  }

  return {
    ...dialog,
    unread_count: 0,
    last_message: {
      ...dialog.last_message,
      read_at: new Date().toUTCString(),
    },
  };
};

const readDialogLastIncomeMessage = (contactId: number) => (dialog: Dialog) => {
  if (
    dialog.last_message.contact_id !== contactId ||
    !dialog.last_message.is_incoming
  ) {
    return dialog;
  }

  return {
    ...dialog,
    unread_count: 0,
    last_message: {
      ...dialog.last_message,
      read_at: new Date().toUTCString(),
    },
  };
};

const messengerSlice = createSlice({
  name: 'messenger',
  initialState,
  reducers: {
    setDialogs(state, action: PayloadAction<DialogsResponse>) {
      state.dialogs = action.payload;
    },
    addDialog(state, action: PayloadAction<Dialog>) {
      state.dialogs.data = [action.payload].concat(state.dialogs.data);
    },
    addDialogs(state, action: PayloadAction<DialogsResponse>) {
      state.dialogs = {
        data: state.dialogs.data.concat(action.payload.data),
        next: action.payload.next,
      };
    },
    updateDialogWithNewMessage(state, action: PayloadAction<Message>) {
      const { payload: newMessage } = action;

      const isExistInDialogs = Boolean(
        state.dialogs.data.find(
          (dialog) => dialog.contact.id === newMessage.contact_id
        )
      );

      if (isExistInDialogs) {
        state.dialogs.data = state.dialogs.data.map(
          updateDialogWithNewMessageMapper(newMessage)
        );
      }
    },
    setMessages(
      state,
      action: PayloadAction<{
        messages: Message[];
        contactId: number;
        contact: User | null;
        next: string | null;
        isMatched: boolean;
      }>
    ) {
      const { messages, contactId, contact, next, isMatched } = action.payload;

      const sortedMsg = sortMessagesByDate(messages);

      state.messages[contactId] = {
        messages: sortedMsg,
        contact,
        isMatched,
        next,
      };
    },
    addMessage(
      state,
      action: PayloadAction<{ message: Message; contactId: number }>
    ) {
      const { message, contactId } = action.payload;

      if (state.messages[contactId] && state.messages[contactId].messages) {
        state.messages[contactId].messages = [
          ...state.messages[contactId].messages,
          message,
        ];
      } else {
        state.messages[contactId] = {
          ...state.messages[contactId],
          messages: [message],
          next: null,
        };
      }
    },
    updateMessage(
      state,
      action: PayloadAction<{ message: Message; contactId: number }>
    ) {
      const { message, contactId } = action.payload;

      if (state.messages[contactId] && state.messages[contactId].messages) {
        state.messages[contactId].messages = state.messages[
          contactId
        ].messages.map((oldMessage) => {
          return oldMessage.id === message.id ? message : oldMessage;
        });
      } else {
        state.messages[contactId] = {
          ...state.messages[contactId],
          messages: [message],
          next: null,
        };
      }
    },
    updateMessageFailedStatus(
      state,
      action: PayloadAction<{
        contactId: number;
        fakeMessageId: number;
        newMessageId?: number;
      }>
    ) {
      const { contactId, fakeMessageId, newMessageId } = action.payload;

      if (state.messages[contactId] && state.messages[contactId].messages) {
        state.messages[contactId].messages = state.messages[
          contactId
        ].messages.map((msg) => {
          if (msg.id === fakeMessageId)
            return {
              ...msg,
              id: newMessageId || fakeMessageId,
              is_failed: true,
            };

          return msg;
        });
      }
    },
    removeMessage(
      state,
      action: PayloadAction<{ messageId: number; contactId: number }>
    ) {
      const { messageId, contactId } = action.payload;

      if (state.messages[contactId] && state.messages[contactId].messages) {
        state.messages[contactId].messages = state.messages[
          contactId
        ].messages.filter((message) => message.id !== messageId);
      }
    },
    addMessages(
      state,
      action: PayloadAction<{
        messages: Message[];
        contactId: number;
        next: string | null;
      }>
    ) {
      const { messages, contactId, next } = action.payload;

      if (state.messages[contactId] && state.messages[contactId].messages) {
        const allMsgs = state.messages[contactId].messages.concat(messages);
        const sortedAllMsgs = sortMessagesByDate(allMsgs);

        state.messages[contactId].messages = sortedAllMsgs;
        state.messages[contactId].next = next;
      } else {
        state.messages[contactId] = {
          ...state.messages[contactId],
          messages,
          next,
        };
      }
    },
    readMessage(
      state,
      action: PayloadAction<{ contactId: number; isIncoming: boolean }>
    ) {
      const {
        payload: { contactId, isIncoming },
      } = action;

      if (!state.messages[contactId] || !state.messages[contactId].messages) {
        return;
      }

      state.messages[contactId].messages = state.messages[
        contactId
      ].messages.map(setMessageReadAt(contactId, isIncoming));
      state.messages[contactId].next = state.messages[contactId].next ?? null;

      state.dialogs.data = state.dialogs.data.map(
        setDialogLastMessageReadAt(contactId)
      );
    },
    readLastDialogIncomeMessage(state, action: PayloadAction<number>) {
      state.dialogs.data = state.dialogs.data.map(
        readDialogLastIncomeMessage(action.payload)
      );
    },
    setMessagesLoading(state, action: PayloadAction<boolean>) {
      state.messagesLoading = action.payload;
    },
  },
});

export const {
  setDialogs,
  addDialogs,
  addDialog,
  updateDialogWithNewMessage,
  setMessages,
  addMessage,
  updateMessage,
  updateMessageFailedStatus,
  removeMessage,
  addMessages,
  readMessage,
  readLastDialogIncomeMessage,
  setMessagesLoading,
} = messengerSlice.actions;

export const messenger = messengerSlice.reducer;
